import React, { useEffect, useMemo } from "react";
import "./Home.css";
import { Link } from "react-router-dom";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import NavigationBar from "../../components/NavigationBar/NavigationBar";
import { Languages } from "../../locales/types";
import AppIcon from "../../components/AppIcon/AppIcon";
import { APP_NAME } from "../../utils/constants";
import { SupportedLanguages } from "../../locales/i18n";
import { APP_GOOGLE_PLAY_URL } from "../../constants/constants";

const HomePage = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, []);

  const changeLanguage = (
    event: React.ChangeEvent<HTMLSelectElement, HTMLSelectElement>,
  ) => {
    i18n.changeLanguage(event.target.value);
  };

  const currentLanguage = useMemo(
    () =>
      (i18n.resolvedLanguage || i18n.language || Languages.EN).split("-")[0],
    [i18n.resolvedLanguage, i18n.language],
  );

  const trackStoreClick = (store: "google_play" | "app_store") => {
    window.fbq?.("track", "Lead", {
      store,
    });
  };

  const trackContentClick = (
    content: "terms_and_conditions" | "privacy" | "data_deletion",
  ) => {
    window.fbq?.("track", "ViewContent", {
      content,
    });
  };

  return (
    <div className="h-full w-full overflow-auto">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#3BA2F4] opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#3BA2F4] opacity-5 rounded-full blur-3xl"></div>
      </div>
      {/* <!-- Navigation --> */}
      <NavigationBar>
        <div className="flex items-center gap-3">
          <select
            aria-label={t("global:choiceLanguage")}
            value={currentLanguage}
            onChange={changeLanguage}
            className="bg-white/5 hover:bg-white/10 border border-white/20 text-white pl-4 py-2.5 rounded-full text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-[#3BA2F4]/40"
          >
            {SupportedLanguages.map((languageOption) => (
              <option
                key={languageOption.languageCode}
                value={languageOption.languageCode}
                className="bg-[#141516] text-white"
              >
                {languageOption.languageName}
              </option>
            ))}
          </select>
          <a
            href={APP_GOOGLE_PLAY_URL}
            onClick={() => trackStoreClick("google_play")}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex items-center gap-2 bg-[#3BA2F4] hover:bg-[#2b8ad4] text-white px-5 py-2.5 rounded-full font-medium transition-all"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3.609 1.814L13.792 12 3.609 22.186c-.181-.181-.29-.432-.29-.714V2.528c0-.282.109-.533.29-.714zm10.831 10.186l2.944-2.944 5.106 2.944-5.106 2.944-2.944-2.944zM4.5 1.1l10.186 10.186L4.5 21.472V1.1zm.836-.523L14.9 9.14l2.32-2.32L5.336.577z" />
            </svg>
            <span id="nav-cta">{t("global:download")}</span>
          </a>
        </div>
      </NavigationBar>
      {/* <!-- Hero Section --> */}
      <section className="relative z-10 px-6 pt-10 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* <!-- Left Content --> */}
            <div className="text-center lg:text-left">
              <div className="fade-in stagger-1">
                <span className="inline-flex items-center gap-2 bg-[#3BA2F4]/10 border border-[#3BA2F4]/20 text-[#3BA2F4] px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                  <span className="w-2 h-2 bg-[#3BA2F4] rounded-full animate-pulse"></span>
                  {t("global:available_on", { platform: "Android" })}
                </span>
              </div>
              <h1
                id="hero-title"
                className="fade-in stagger-2 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
              >
                {t("global:yourVoice")} <br />
                <span className="gradient-text">
                  {t("global:timeAssistant")}
                </span>
              </h1>
              <p
                id="hero-subtitle"
                className="fade-in stagger-3 text-[#9CA3AF] text-lg sm:text-xl mb-8 max-w-xl mx-auto lg:mx-0"
              >
                {t("global:yourVoiceTimeAssistantDescription")}
              </p>
              {/* <!-- Language Pills --> */}
              <div className="fade-in stagger-4 flex flex-wrap justify-center lg:justify-start gap-2 mb-8">
                {SupportedLanguages.map((language) => (
                  <span className="language-pill px-3 py-1.5 rounded-full text-sm text-white/80">
                    {language.languageName}
                  </span>
                ))}
              </div>
              {/* <!-- CTA Buttons --> */}
              <div className="fade-in stagger-5 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href={APP_GOOGLE_PLAY_URL}
                  onClick={() => trackStoreClick("google_play")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-center gap-3 bg-[#3BA2F4] hover:bg-[#2b8ad4] text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all hover:scale-105 glow-effect"
                >
                  <svg
                    className="w-7 h-7"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M3.609 1.814L13.792 12 3.609 22.186c-.181-.181-.29-.432-.29-.714V2.528c0-.282.109-.533.29-.714zm10.831 10.186l2.944-2.944 5.106 2.944-5.106 2.944-2.944-2.944zM4.5 1.1l10.186 10.186L4.5 21.472V1.1zm.836-.523L14.9 9.14l2.32-2.32L5.336.577z" />
                  </svg>
                  <span id="cta-button">
                    {t("global:downloadFrom", { platform: "Google Play" })}
                  </span>
                </a>
              </div>
              {/* <!-- Stats --> */}
              <div className="fade-in stagger-6 flex gap-8 justify-center lg:justify-start mt-10 pt-10 border-t border-white/10">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white font-heading">
                    {Object.keys(Languages).length}
                  </div>
                  <div className="text-[#9CA3AF] text-sm">
                    {_.capitalize(
                      t("global:languagesWord", {
                        count: Object.keys(Languages).length,
                      }),
                    )}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white font-heading">
                    ∞
                  </div>
                  <div className="text-[#9CA3AF] text-sm">
                    {t("global:intervals")}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white font-heading">
                    1
                  </div>
                  <div className="text-[#9CA3AF] text-sm">
                    {t("global:oneWakeUpIntegration")}
                  </div>
                </div>
              </div>
            </div>
            {/* <!-- Right Content - Phone Mockups --> */}
            <div className="relative flex flex-col sm:flex-row justify-center items-center gap-6">
              {/* <!-- Android Phone --> */}
              <div className="phone-frame w-56 sm:w-64 float-animation glow-effect">
                <div className="phone-screen aspect-[9/19.5] flex flex-col">
                  {/* <!-- Status Bar --> */}
                  <div className="flex justify-between items-center px-5 py-3 text-white/60 text-xs">
                    <span>12:34</span>
                    <div className="flex gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 21l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.18L12 21z" />
                      </svg>
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z" />
                      </svg>
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z" />
                      </svg>
                    </div>
                  </div>
                  {/* <!-- App Content --> */}
                  <div className="flex-1 flex flex-col items-center justify-center px-4 pb-8">
                    {/* <!-- Timer Display --> */}
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-[#3BA2F4]/20 rounded-full pulse-ring"></div>
                      <div className="relative w-32 h-32 sm:w-36 sm:h-36 rounded-full border-4 border-[#3BA2F4] flex items-center justify-center bg-[#3BA2F4]/5">
                        <div className="text-center">
                          <div className="text-2xl sm:text-3xl font-bold text-white font-heading">
                            15:00
                          </div>
                          <div className="text-[#3BA2F4] text-xs mt-1">
                            {t("global:timer").toUpperCase()}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* <!-- Voice Bubble --> */}
                    <div className="bg-[#3BA2F4]/20 border border-[#3BA2F4]/30 rounded-2xl px-4 py-2.5 mb-6">
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-[#3BA2F4]"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z" />
                        </svg>
                        <span className="text-white text-xs font-medium">
                          {t("global:remainingTime", { time: "15" })}
                        </span>
                      </div>
                    </div>
                    {/* <!-- Control Buttons --> */}
                    <div className="flex gap-3">
                      <button className="w-12 h-12 rounded-full bg-[#3BA2F4] flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-white"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                        </svg>
                      </button>
                      <button className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-white"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M6 6h12v12H6z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* <!-- iPhone Frame - Coming Soon --> */}
              <div className="lg:block phone-frame w-52 opacity-60 relative">
                <div className="phone-screen aspect-[9/19.5] flex flex-col items-center justify-center">
                  <div className="ios-badge rounded-2xl px-6 py-8 text-center mx-4">
                    <svg
                      className="w-12 h-12 text-white/40 mx-auto mb-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                    </svg>
                    <div className="text-white/80 font-heading font-semibold mb-2">
                      iOS
                    </div>
                    <div className="text-white/50 text-sm">
                      {t("global:availableSoon")}
                    </div>
                    <div className="mt-4 text-[#3BA2F4] text-xs">
                      {t("global:workingOnIt")}
                    </div>
                  </div>
                </div>
                {/* <!-- iOS Label --> */}
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <span className="text-white/40 text-sm">
                    {t("global:comingSoon")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <!-- Features Section --> */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2
              id="features-title"
              className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4"
            >
              {t("global:functionalitiesBeLoved")}
            </h2>
            <p className="text-[#9CA3AF] text-lg max-w-2xl mx-auto">
              {t("global:whyBeLoved")}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* <!-- Feature 1 --> */}
            <div className="feature-card rounded-3xl p-6">
              <div className="w-14 h-14 rounded-2xl bg-[#3BA2F4]/20 flex items-center justify-center mb-5">
                <svg
                  className="w-7 h-7 text-[#3BA2F4]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                  />
                </svg>
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                {t("global:voiceNotifications")}
              </h3>
              <p className="text-[#9CA3AF]">
                {t("global:voiceNotificationsDescription")}
              </p>
            </div>
            {/* <!-- Feature 2 --> */}
            <div className="feature-card rounded-3xl p-6">
              <div className="w-14 h-14 rounded-2xl bg-[#3BA2F4]/20 flex items-center justify-center mb-5">
                <svg
                  className="w-7 h-7 text-[#3BA2F4]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                {t("global:alarmTimeAnnouncement")}
              </h3>
              <p className="text-[#9CA3AF]">
                {t("global:alarmTimeAnnouncementDescription")}
              </p>
            </div>
            <div className="feature-card rounded-3xl p-6">
              <div className="w-14 h-14 rounded-2xl bg-[#3BA2F4]/20 flex items-center justify-center mb-5">
                <svg
                  className="w-7 h-7 text-[#3BA2F4]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 7l3-3h12l3 3v12l-3 3H6l-3-3V7zm5 4h.01M12 9h4m-8 6l2.5-2.5a2 2 0 012.828 0L16 15m-8 0h8"
                  />
                </svg>
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                {t("global:photoChallenge")}
              </h3>
              <p className="text-[#9CA3AF]">
                {t("global:photoChallengeDescription")}
              </p>
            </div>
            {/* <!-- Feature 4 --> */}
            <div className="feature-card rounded-3xl p-6">
              <div className="w-14 h-14 rounded-2xl bg-[#3BA2F4]/20 flex items-center justify-center mb-5">
                <svg
                  className="w-7 h-7 text-[#3BA2F4]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                {t("global:customIntervals")}
              </h3>
              <p className="text-[#9CA3AF]">
                {t("global:customIntervalsDescription")}
              </p>
            </div>
            {/* <!-- Feature 5 --> */}
            <div className="feature-card rounded-3xl p-6">
              <div className="w-14 h-14 rounded-2xl bg-[#3BA2F4]/20 flex items-center justify-center mb-5">
                <svg
                  className="w-7 h-7 text-[#3BA2F4]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                {Object.keys(Languages).length}{" "}
                {t("global:languagesWord", {
                  count: Object.keys(Languages).length,
                })}
              </h3>
              <p className="text-[#9CA3AF]">
                English, Deutsch, Español, Français, Polski, Norsk{" "}
                {t("global:and")} Svenska {t("global:languagesAvailable")}
              </p>
            </div>
            {/* <!-- Feature 6 --> */}
            <div className="feature-card rounded-3xl p-6">
              <div className="w-14 h-14 rounded-2xl bg-[#3BA2F4]/20 flex items-center justify-center mb-5">
                <svg
                  className="w-7 h-7 text-[#3BA2F4]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                {t("global:getOutOfBed")}
              </h3>
              <p className="text-[#9CA3AF]">{t("global:photoFunction")}</p>
            </div>
          </div>
        </div>
      </section>
      <section className="relative z-10 px-6 py-10">
        <div className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl border border-[#3BA2F4]/20 bg-gradient-to-br from-[#3BA2F4]/10 via-[#3BA2F4]/5 to-transparent p-8 sm:p-10">
            <div className="absolute top-0 right-0 h-40 w-40 rounded-full bg-[#3BA2F4]/10 blur-3xl"></div>
            <div className="relative grid lg:grid-cols-[1.2fr_0.8fr] gap-8 items-center">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-[#3BA2F4]/25 bg-[#3BA2F4]/10 px-4 py-1.5 text-sm font-medium text-[#3BA2F4] mb-5">
                  <span className="h-2 w-2 rounded-full bg-[#3BA2F4] animate-pulse"></span>
                  {t("global:availableNowInApp")}
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
                  {t("global:photoChallengeSpotlight")}
                </h2>
                <p className="text-[#9CA3AF] text-lg max-w-2xl">
                  {t("global:photoChallengeSpotlightDescription")}
                </p>
              </div>
              <div className="relative">
                <div className="rounded-[2rem] border border-white/10 bg-[#0f1012]/80 p-5 shadow-[0_20px_60px_rgba(59,162,244,0.12)]">
                  <div className="mb-4 flex items-center justify-between text-xs text-white/50">
                    <span>07:00</span>
                    <span>Talking Alarm Clock</span>
                  </div>
                  <div className="rounded-3xl border border-[#3BA2F4]/20 bg-[#3BA2F4]/5 p-5">
                    <div className="mb-4 flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#3BA2F4]/20">
                        <svg
                          className="w-6 h-6 text-[#3BA2F4]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M3 7l3-3h12l3 3v12l-3 3H6l-3-3V7zm5 4h.01M12 9h4m-8 6l2.5-2.5a2 2 0 012.828 0L16 15m-8 0h8"
                          />
                        </svg>
                      </div>
                      <div>
                        <div className="text-white font-semibold">
                          {t("global:photoChallenge")}
                        </div>
                        <div className="text-sm text-[#9CA3AF]">
                          {t("global:photoChallengeExamples")}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {["sink", "coffee", "door"].map((item) => (
                        <div
                          key={item}
                          className="aspect-square rounded-2xl border border-white/10 bg-white/5"
                        />
                      ))}
                    </div>
                    <div className="flex items-center justify-between rounded-2xl border border-[#3BA2F4]/20 bg-black/20 px-4 py-3">
                      <span className="text-sm text-white/80">
                        {t("global:targetPhoto")}
                      </span>
                      <span className="text-sm font-medium text-[#3BA2F4]">
                        {t("global:alarmActive")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <!-- Problems Section --> */}
      <section className="relative z-10 px-6 py-20 bg-gradient-to-b from-transparent via-[#3BA2F4]/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2
              id="problems-title"
              className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4"
            >
              {t("global:problemsWeKnowTooWell")}
            </h2>
            <p className="text-[#9CA3AF] text-lg max-w-2xl mx-auto">
              {t("global:problemsWeKnowTooWellDescription")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {/* <!-- Problem 1 --> */}
            <div className="group relative rounded-3xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative border border-red-500/20 group-hover:border-red-500/40 rounded-3xl p-8 transition-all duration-300 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <svg
                      className="w-6 h-6 text-red-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-semibold text-white mb-2">
                      {t("global:asleep")}
                    </h3>
                    <p className="text-[#9CA3AF] text-sm">
                      {t("global:asleepDescription")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* <!-- Problem 2 --> */}
            <div className="group relative rounded-3xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative border border-orange-500/20 group-hover:border-orange-500/40 rounded-3xl p-8 transition-all duration-300 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <svg
                      className="w-6 h-6 text-orange-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 12c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-semibold text-white mb-2">
                      {t("global:multipleAlarmsNoClear")}
                    </h3>
                    <p className="text-[#9CA3AF] text-sm">
                      {t("global:multipleAlarmsNoClearDescription")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* <!-- Problem 3 --> */}
            <div className="group relative rounded-3xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/10 to-yellow-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative border border-yellow-500/20 group-hover:border-yellow-500/40 rounded-3xl p-8 transition-all duration-300 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <svg
                      className="w-6 h-6 text-yellow-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 9v2m0 4v2m0 4v2M6.34 7.34a9 9 0 1112.73 0M6.34 7.34l-3.54-3.54m3.54 3.54L12 3m0 0l3.66-3.66m-3.66 3.66L21 0m-6.34 7.34l3.54-3.54m-3.54 3.54l-3.66 3.66"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-semibold text-white mb-2">
                      {t("global:systemAlarmsStopWorking")}
                    </h3>
                    <p className="text-[#9CA3AF] text-sm">
                      {t("global:systemAlarmsStopWorkingDescription")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* <!-- Problem 4 --> */}
            <div className="group relative rounded-3xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative border border-pink-500/20 group-hover:border-pink-500/40 rounded-3xl p-8 transition-all duration-300 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-pink-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <svg
                      className="w-6 h-6 text-pink-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-semibold text-white mb-2">
                      {t("global:canNotGetOutOfBed")}
                    </h3>
                    <p className="text-[#9CA3AF] text-sm">
                      {t("global:canNotGetOutOfBedDescription")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* <!-- Problem 5 --> */}
            <div className="group relative rounded-3xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative border border-purple-500/20 group-hover:border-purple-500/40 rounded-3xl p-8 transition-all duration-300 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <svg
                      className="w-6 h-6 text-purple-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-semibold text-white mb-2">
                      {t("global:allAlarmsWithin5Minutes")}
                    </h3>
                    <p className="text-[#9CA3AF] text-sm">
                      {t("global:allAlarmsWithin5MinutesDescription")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* <!-- Solution --> */}
            <div className="group relative rounded-3xl overflow-hidden md:col-span-2">
              <div className="absolute inset-0 bg-gradient-to-br from-[#3BA2F4]/20 to-[#3BA2F4]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative border border-[#3BA2F4]/30 group-hover:border-[#3BA2F4]/60 rounded-3xl p-8 transition-all duration-300 backdrop-blur-sm bg-[#3BA2F4]/5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#3BA2F4]/30 flex items-center justify-center flex-shrink-0 mt-1">
                    <svg
                      className="w-6 h-6 text-[#3BA2F4]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m7 7a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-semibold text-white mb-2">
                      ✨ {t("global:solution")}: {APP_NAME}
                    </h3>
                    <p className="text-[#9CA3AF] text-sm mb-4">
                      {t("global:solutionDescription")}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <span className="inline-flex items-center gap-2 text-sm text-[#3BA2F4]">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd"
                          />
                        </svg>
                        {t("global:willRemember")}
                      </span>
                      <span className="inline-flex items-center gap-2 text-sm text-[#FFFFFF]">
                        {t("global:or")}
                      </span>
                      <span className="inline-flex items-center gap-2 text-sm text-[#3BA2F4]">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd"
                          />
                        </svg>
                        {t("global:willHaveToGetOutOfBed")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <!-- Upcoming Features Section --> */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2
              id="upcoming-title"
              className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4"
            >
              {t("global:wakeUpIntegration")}
            </h2>
            <p className="text-[#9CA3AF] text-lg max-w-2xl mx-auto">
              {t("global:wakeUpIntegrationDescription")}
            </p>
          </div>
          {/* <div className="grid md:grid-cols-1 gap-8"> */}
          {/* <!-- Feature 1: Photo Challenge --> */}
          <div className="group relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative border border-cyan-500/20 group-hover:border-cyan-500/40 rounded-3xl p-8 transition-all duration-300 backdrop-blur-sm">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-6 h-6 text-cyan-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 7l3-3h12l3 3v12l-3 3H6l-3-3V7zm5 4h.01M12 9h4m-8 6l2.5-2.5a2 2 0 012.828 0L16 15m-8 0h8"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-white">
                    {t("global:photoChallenge")}
                  </h3>
                  <p className="text-cyan-400 text-sm mt-1">
                    ✨ {t("global:availableNowInApp")}
                  </p>
                </div>
              </div>
              <p className="text-[#9CA3AF] text-sm mb-4">
                {t("global:photoChallengeDescription")}
              </p>
              <div className="flex items-center gap-2 text-cyan-400 text-sm">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                {t("global:photoChallengeExamples")}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <!-- Upcoming Features Section --> */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              {t("global:upcomingFeatures")}
            </h2>
            <p className="text-[#9CA3AF] text-lg max-w-2xl mx-auto">
              {t("global:upcomingFeaturesDescription")}
            </p>
          </div>
          <div className="group relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-500/10 to-slate-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative border border-slate-500/20 group-hover:border-slate-500/40 rounded-3xl p-8 transition-all duration-300 backdrop-blur-sm">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-slate-500/20 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-6 h-6 text-slate-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 3h8M8 21h8M9 3c0 3 2 4 3 5-1 1-3 2-3 5s2 4 3 5c1-1 3-2 3-5s-2-4-3-5c1-1 3-2 3-5"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M10 8h4M10 16h4"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-white">
                    {t("global:iosSupport")}
                  </h3>
                  <p className="text-slate-300 text-sm mt-1">
                    ⏳ {t("global:comingSoon")}
                  </p>
                </div>
              </div>
              <p className="text-[#9CA3AF] text-sm mb-4">
                {t("global:iosSupportDescription")}
              </p>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                {t("global:iosSupportRoadmap")}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <!-- CTA Section --> */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden">
            {/* <!-- Background --> */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#3BA2F4]/20 to-[#3BA2F4]/5"></div>
            <div className="absolute inset-0 border border-[#3BA2F4]/20 rounded-3xl"></div>
            <div className="relative px-8 py-16 text-center">
              <div className="w-20 h-20 rounded-full bg-[#3BA2F4]/20 flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-10 h-10 text-[#3BA2F4]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
                {t("global:readyToNotBeLate")}
              </h2>
              <p className="text-[#9CA3AF] text-lg mb-8 max-w-xl mx-auto">
                {t("global:readyToDownload")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={APP_GOOGLE_PLAY_URL}
                  onClick={() => trackStoreClick("google_play")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-[#3BA2F4] hover:bg-[#2b8ad4] text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all hover:scale-105"
                >
                  <svg
                    className="w-6 h-6"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M3.609 1.814L13.792 12 3.609 22.186c-.181-.181-.29-.432-.29-.714V2.528c0-.282.109-.533.29-.714zm10.831 10.186l2.944-2.944 5.106 2.944-5.106 2.944-2.944-2.944zM4.5 1.1l10.186 10.186L4.5 21.472V1.1zm.836-.523L14.9 9.14l2.32-2.32L5.336.577z" />
                  </svg>
                  Google Play
                </a>
                <div className="inline-flex items-center justify-center gap-3 bg-white/5 border border-white/20 text-white/60 px-8 py-4 rounded-2xl font-semibold text-lg cursor-not-allowed">
                  iOS – {_.capitalize(t("global:soon"))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <!-- Footer --> */}
      <footer className="relative z-10 px-6 py-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <AppIcon
            appName={`${APP_NAME} © 2026`}
            appNameStyle="text-white/60 text-sm"
          />
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Link
              to="/terms-and-conditions"
              onClick={() => trackContentClick("terms_and_conditions")}
              className="inline-flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/20 text-white/80 px-4 py-2 rounded-xl text-sm font-medium transition-all"
            >
              {t("global:termsAndConditions")}
            </Link>
            <Link
              to="/privacy-policy"
              onClick={() => trackContentClick("privacy")}
              className="inline-flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/20 text-white/80 px-4 py-2 rounded-xl text-sm font-medium transition-all"
            >
              {t("global:privacyPolicy")}
            </Link>
            <Link
              to="/data-deletion"
              onClick={() => trackContentClick("data_deletion")}
              className="inline-flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/20 text-white/80 px-4 py-2 rounded-xl text-sm font-medium transition-all"
            >
              {t("global:dataDeletion")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
